import { 
  collection, 
  doc, 
  addDoc, 
  setDoc,
  updateDoc, 
  deleteDoc, 
  getDocs, 
  getDoc, 
  query, 
  where, 
  orderBy, 
  limit,
  serverTimestamp 
} from 'firebase/firestore';
import { db } from './config';
import { User, Category, Product, Cart, Order } from '../types';
import { ServiceArea } from '../utils/serviceArea';
import { cache } from '../utils/cache';
import { trackPurchase } from '../utils/analytics';

export const createUser = async (userData: Omit<User, 'id'>, userId?: string): Promise<string> => {
  if (!db) {
    throw new Error('Firebase not configured');
  }
  
  try {
    if (userId) {
      const docRef = doc(db, 'users', userId);
      await setDoc(docRef, {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        role: 'customer'
      });
      return userId;
    } else {
      const docRef = await addDoc(collection(db, 'users'), {
        ...userData,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        isActive: true,
        role: 'customer'
      });
      return docRef.id;
    }
  } catch (error: any) {
    console.error('Error creating user:', error);
    throw error;
  }
};

export const getUser = async (userId: string): Promise<User | null> => {
  if (!db) {
    return null;
  }
  
  try {
    const docRef = doc(db, 'users', userId);
    const docSnap = await getDoc(docRef);
    return docSnap.exists() ? { id: docSnap.id, ...docSnap.data() } as User : null;
  } catch (error: any) {
    console.error('Error getting user:', error);
    
    if (error.code === 'unavailable' || error.message?.includes('offline')) {
      return null;
    }
    
    throw error;
  }
};

export const updateUser = async (userId: string, userData: Partial<User>): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not configured');
  }
  
  try {
    const docRef = doc(db, 'users', userId);
    await updateDoc(docRef, {
      ...userData,
      updatedAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error updating user:', error);
    throw error;
  }
};

export const getCategories = async (): Promise<Category[]> => {
  if (!db) {
    console.error('Firebase not configured');
    return [];
  }
  
  const cacheKey = 'categories_all';
  const cached = cache.get<Category[]>(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    const q = query(
      collection(db, 'categories'), 
      where('isActive', '==', true),
      orderBy('sortOrder', 'asc')
    );
    const querySnapshot = await getDocs(q);
    const categories = querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Category));
    
    cache.set(cacheKey, categories, 10 * 60 * 1000);
    
    return categories;
  } catch (error: any) {
    console.error('Error getting categories:', error);
    
    if (error.code === 'failed-precondition') {
      console.warn('Index missing - trying without orderBy...');
      try {
        const simpleQ = query(
          collection(db, 'categories'), 
          where('isActive', '==', true)
        );
        const fallbackSnapshot = await getDocs(simpleQ);
        const fallbackCategories = fallbackSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as Category));
        
        return fallbackCategories;
      } catch (fallbackError) {
        console.error('Fallback query also failed:', fallbackError);
      }
    }
    
    if (error.code === 'unavailable' || error.message?.includes('offline')) {
      return [
        { id: '1', name: 'Vegetables', description: 'Fresh vegetables', image: '', isActive: true, sortOrder: 1 },
        { id: '2', name: 'Fruits', description: 'Fresh fruits', image: '', isActive: true, sortOrder: 2 },
        { id: '3', name: 'Grains', description: 'Organic grains', image: '', isActive: true, sortOrder: 3 }
      ];
    }
    
    return [];
  }
};

export const getSeasonalProducts = async (): Promise<Product[]> => {
  if (!db) {
    console.error('Firebase not configured');
    return [];
  }
  
  const cacheKey = 'products_seasonal';
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    const q = query(
      collection(db, 'products'),
      where('isSeasonal', '==', true),
      where('isAvailable', '==', true)
    );
    
    const querySnapshot = await getDocs(q);
    const products = querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Product));
    
    cache.set(cacheKey, products, 5 * 60 * 1000);
    
    return products;
  } catch (error: any) {
    console.error('Error getting seasonal products:', error);
    if (error.code === 'permission-denied') {
      console.error('Permission denied for seasonal products - check Firestore rules');
    }
    return [];
  }
};

export const getProducts = async (categoryId: string | null = null): Promise<Product[]> => {
  if (!db) {
    console.error('Firebase not configured');
    return [];
  }
  
  const cacheKey = categoryId ? `products_category_${categoryId}` : 'products_all';
  const cached = cache.get<Product[]>(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    let q;
    if (categoryId) {
      q = query(
        collection(db, 'products'),
        where('categoryId', '==', categoryId),
        where('isAvailable', '==', true)
      );
    } else {
      q = query(
        collection(db, 'products'),
        where('isAvailable', '==', true)
      );
    }
    
    const querySnapshot = await getDocs(q);
    let products = querySnapshot.docs.map(doc => ({ 
      id: doc.id, 
      ...doc.data() 
    } as Product));
    
    if (categoryId && products.length === 0) {
      try {
        const fallbackQ = query(
          collection(db, 'products'),
          where('category', '==', categoryId),
          where('isAvailable', '==', true)
        );
        const fallbackSnapshot = await getDocs(fallbackQ);
        products = fallbackSnapshot.docs.map(doc => ({ 
          id: doc.id, 
          ...doc.data() 
        } as Product));
      } catch (fallbackError) {
        console.warn('Fallback query failed:', fallbackError);
      }
    }
    
    if (categoryId && products.length === 0) {
      console.warn(`⚠️ No products found for category: ${categoryId}`);
    }
    
    cache.set(cacheKey, products, 5 * 60 * 1000);
    
    return products;
  } catch (error: any) {
    console.error('Error getting products:', error);
    
    if (error.code === 'permission-denied') {
      console.error('🚫 Permission denied for products - check authentication and Firestore rules');
    }
    
    if (error.code === 'unavailable' || error.message?.includes('offline')) {
      const demoProducts = [
        { 
          id: '1', 
          name: 'Fresh Tomatoes', 
          description: 'Organic red tomatoes',
          price: 40,
          unit: 'kg',
          category: categoryId || '1',
          isAvailable: true,
          images: [],
          stockQuantity: 100,
          isOrganic: true
        },
        { 
          id: '2', 
          name: 'Green Apples', 
          description: 'Fresh green apples',
          price: 120,
          unit: 'kg',
          category: categoryId || '2',
          isAvailable: true,
          images: [],
          stockQuantity: 50,
          isOrganic: true
        }
      ];
      
      return categoryId ? demoProducts.filter(p => p.category === categoryId) : demoProducts;
    }
    
    return [];
  }
};

export const getServiceAreas = async (): Promise<ServiceArea[]> => {
  if (!db) {
    console.error('Firebase not configured');
    return [];
  }

  const cacheKey = 'service_areas';
  const cached = cache.get<ServiceArea[]>(cacheKey);
  if (cached) {
    return cached;
  }

  try {
    const q = query(
      collection(db, 'service_areas'),
      where('isActive', '==', true)
    );
    const snapshot = await getDocs(q);
    const areas = snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        name: data.name,
        centerLat: parseFloat(data.centerLat),
        centerLng: parseFloat(data.centerLng),
        radiusKm: parseFloat(data.radiusKm),
        isActive: data.isActive
      } as ServiceArea;
    });

    cache.set(cacheKey, areas, 10 * 60 * 1000);
    return areas;
  } catch (error) {
    console.error('Error fetching service areas:', error);
    return [];
  }
};

export const createOrUpdateCart = async (userId: string, cartData: any): Promise<void> => {
  if (!db) {
    throw new Error('Firebase not configured');
  }
  
  try {
    const cartRef = doc(db, 'carts', userId);
    const cartSnap = await getDoc(cartRef);
    
    if (cartSnap.exists()) {
      await updateDoc(cartRef, {
        items: cartData.items,
        updatedAt: serverTimestamp()
      });
    } else {
      await setDoc(cartRef, {
        userId,
        items: cartData.items,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
    }
  } catch (error: any) {
    console.error('Error creating/updating cart:', error);
    
    if (error.code === 'permission-denied') {
      console.warn('Cart save denied - user may not be authenticated');
      return;
    }
    
    throw error;
  }
};

export const getCart = async (userId: string): Promise<any> => {
  if (!db) {
    return null;
  }
  
  try {
    const cartRef = doc(db, 'carts', userId);
    const cartSnap = await getDoc(cartRef);
    
    if (cartSnap.exists()) {
      return { id: cartSnap.id, ...cartSnap.data() };
    } else {
      return null;
    }
  } catch (error: any) {
    console.error('Error getting cart:', error);
    
    if (error.code === 'permission-denied') {
      console.warn('Cart access denied - user may not be authenticated');
      return null;
    }
    
    throw error;
  }
};

export const createOrder = async (orderData: any): Promise<string> => {
  if (!db) {
    throw new Error('Firebase not configured');
  }
  
  try {
    const trackingId = generateTrackingId();
    const orderToCreate = {
      ...orderData,
      orderDate: serverTimestamp(),
      updatedAt: serverTimestamp(),
      status: 'pending',
      deliveryTimeSlot: '6:00 AM - 9:00 AM',
      trackingId
    };
    
    const docRef = await addDoc(collection(db, 'orders'), orderToCreate);
    
    // Track purchase in GA4 (CONVERSION EVENT) - non-blocking
    try {
      trackPurchase({
        transactionId: trackingId,
        value: orderData.totalAmount || 0,
        items: (orderData.items || []).map((item: any) => ({
          id: item.productId || item.id,
          name: item.productName || item.name || 'Unknown Product',
          category: item.categoryId,
          price: item.price || 0,
          quantity: item.quantity || 1
        }))
      });
    } catch (analyticsError) {
      console.warn('[Analytics] Purchase tracking failed (non-blocking):', analyticsError);
    }
    
    return docRef.id;
  } catch (error) {
    console.error('❌ Error creating order:', error);
    throw error;
  }
};

export const getUserOrders = async (userId: string): Promise<Order[]> => {
  if (!db) {
    console.error('Firebase not configured');
    return [];
  }
  
  try {
    const simpleQuery = query(
      collection(db, 'orders'),
      where('userId', '==', userId)
    );
    const simpleSnapshot = await getDocs(simpleQuery);
    
    if (simpleSnapshot.docs.length === 0) {
      return [];
    }
    
    let orders: Order[] = [];
    try {
      const q = query(
        collection(db, 'orders'),
        where('userId', '==', userId),
        orderBy('orderDate', 'desc')
      );
      const querySnapshot = await getDocs(q);
      orders = querySnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          orderDate: data.orderDate,
          updatedAt: data.updatedAt,
          deliveredAt: data.deliveredAt,
          cancelledAt: data.cancelledAt
        } as Order;
      });
    } catch (orderByError: any) {
      console.warn('⚠️ OrderBy query failed, falling back to simple query:', orderByError.message);
      
      const fallbackSnapshot = await getDocs(simpleQuery);
      orders = fallbackSnapshot.docs.map(doc => {
        const data = doc.data();
        return { 
          id: doc.id, 
          ...data,
          orderDate: data.orderDate,
          updatedAt: data.updatedAt,
          deliveredAt: data.deliveredAt,
          cancelledAt: data.cancelledAt
        } as Order;
      });
      
      orders.sort((a, b) => {
        const dateA = a.orderDate?.toDate ? a.orderDate.toDate() : new Date(a.orderDate);
        const dateB = b.orderDate?.toDate ? b.orderDate.toDate() : new Date(b.orderDate);
        return dateB.getTime() - dateA.getTime();
      });
    }
    
    return orders;
  } catch (error: any) {
    console.error('Error getting user orders:', error);
    
    if (error.code === 'permission-denied') {
      console.error('🚫 Permission denied for orders - check authentication and Firestore rules');
    }
    
    if (error.code === 'unavailable' || error.message?.includes('offline')) {
      return [];
    }
    
    console.error('Error getting user orders:', error);
    return [];
  }
};
export const getOrder = async (orderId: string): Promise<Order | null> => {
  if (!db) {
    console.error('Firebase not configured');
    return null;
  }
  
  try {
    const docRef = doc(db, 'orders', orderId);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      return { id: docSnap.id, ...docSnap.data() } as Order;
    }
    return null;
  } catch (error) {
    console.error('Error getting order:', error);
    return null;
  }
};

export const getProduct = async (productId: string): Promise<Product | null> => {
  if (!db) {
    console.error('Firebase not configured');
    return null;
  }

  if (!productId || typeof productId !== 'string' || productId.trim() === '') {
    console.error('Invalid product ID:', productId);
    return null;
  }
  
  const cacheKey = `product_${productId}`;
  const cached = cache.get<Product>(cacheKey);
  if (cached) {
    return cached;
  }
  
  try {
    const docRef = doc(db, 'products', productId.trim());
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists()) {
      const product = { 
        id: docSnap.id, 
        ...docSnap.data() 
      } as Product;
      
      cache.set(cacheKey, product, 5 * 60 * 1000);
      return product;
    } else {
      return null;
    }
  } catch (error: any) {
    console.error('Error getting product:', error);
    return null;
  }
};

export const updateOrderStatus = async (orderId: string, status: string): Promise<boolean> => {
  if (!db) {
    throw new Error('Firebase not configured');
  }
  
  try {
    const docRef = doc(db, 'orders', orderId);
    await updateDoc(docRef, {
      status,
      updatedAt: serverTimestamp(),
      ...(status === 'delivered' && { deliveredAt: serverTimestamp() }),
      ...(status === 'cancelled' && { cancelledAt: serverTimestamp() })
    });
    
    return true;
  } catch (error) {
    console.error('Error updating order status:', error);
    return false;
  }
};

const generateTrackingId = () => {
  return 'SK' + Date.now().toString().slice(-8) + Math.random().toString(36).substr(2, 4).toUpperCase();
};
